import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QRScanner from './QRScanner';
import FloorMap from './FloorMap';
import floorLayouts from '../data/floorLayouts';

function MissionInterface({ userId, userName, onComplete }) {
  const [missionId, setMissionId] = useState(null);
  const [phase, setPhase] = useState('init'); // init, waiting_first_scan, revealing, navigating, completed
  const [startCharacter, setStartCharacter] = useState(null);
  const [targetCharacter, setTargetCharacter] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [showTarget, setShowTarget] = useState(false);
  const [currentFloor, setCurrentFloor] = useState(1);
  const [floorPath, setFloorPath] = useState(null);
  const [fullRoute, setFullRoute] = useState([]);

  const startMission = async () => {
    try {
      const response = await axios.post('/api/mission/start', { userId });
      setMissionId(response.data.missionId);
      setPhase('waiting_first_scan');
    } catch (err) {
      console.error('Failed to start mission:', err);
      setScanError('Failed to start mission');
    }
  };

  const fetchFloorPath = async (floor) => {
    if (!missionId) return;
    
    try {
      const response = await axios.post('/api/mission/floor-path', {
        missionId,
        currentFloor: floor
      });
      setFloorPath(response.data);
    } catch (err) {
      console.error('Failed to fetch floor path:', err);
    }
  };

  useEffect(() => {
    startMission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch floor path when floor changes or route is assigned
  useEffect(() => {
    if (phase === 'navigating' && missionId) {
      fetchFloorPath(currentFloor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFloor, phase, missionId]);

  const handleTimeExpired = () => {
    alert('Time expired!');
    if (window.logout) {
      window.logout();
    }
  };

  // Timer countdown
  useEffect(() => {
    if (phase === 'navigating' && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleTimeExpired();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeRemaining]);

  const handleScan = async (characterId) => {
    setScanError('');
    setSuccessMessage('');
    setIsScanning(true);
    
    if (!missionId) {
      setScanError('Mission not ready');
      setIsScanning(false);
      return;
    }
    
    try {
      const response = await axios.post('/api/mission/scan', {
        missionId,
        characterId
      });

      if (response.data.phase === 'route_assigned') {
        // First scan - route computed
        setStartCharacter(response.data.startCharacter);
        setTargetCharacter(response.data.targetCharacter);
        setTimeRemaining(response.data.totalTime);
        setFullRoute(response.data.route || []);
        setCurrentFloor(response.data.startCharacter.floor);
        setSuccessMessage('Scanned: ' + response.data.startCharacter.name);
        
        // Close scanner and show revealing animation
        setTimeout(() => {
          setShowScanner(false);
          setIsScanning(false);
          setPhase('revealing');
          
          // Show target after 2 seconds
          setTimeout(() => {
            setShowTarget(true);
            
            // Start navigation phase after another 2 seconds
            setTimeout(() => {
              setPhase('navigating');
            }, 2000);
          }, 2000);
        }, 1000);
      } else if (response.data.phase === 'completed') {
        // Target reached - mission complete
        setSuccessMessage(response.data.message);
        setPhase('completed');
        setTimeout(() => {
          setShowScanner(false);
          setIsScanning(false);
          onComplete();
        }, 2000);
      } else if (response.data.phase === 'time_expired') {
        // Time ran out
        alert(response.data.message);
        if (window.logout) {
          window.logout();
        }
      } else {
        // Wrong character or intermediate scan
        setIsScanning(false);
        setScanError(response.data.message);
        if (response.data.remainingTime !== undefined) {
          setTimeRemaining(response.data.remainingTime);
        }
      }
    } catch (err) {
      setIsScanning(false);
      console.error('Scan error:', err);
      setScanError(err.response?.data?.error || 'Scan failed');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Phase: Waiting for first scan
  if (phase === 'waiting_first_scan') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="scanline"></div>
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">📍</div>
          <h1 className="text-3xl font-bold text-primary mb-4 font-mono">
            SCAN A CHARACTER
          </h1>
          <p className="text-cyber-blue mb-8 font-mono text-sm">
            Scan any QR code to start
          </p>
          <button
            onClick={() => setShowScanner(true)}
            className="bg-primary text-dark font-bold font-mono text-xl
                     py-6 px-12 rounded-lg hover:shadow-neon-green transform hover:scale-105 
                     transition-all duration-200 border-2 border-primary
                     hover:bg-transparent hover:text-primary w-full"
          >
            SCAN →
          </button>
        </div>

        {showScanner && (
          <QRScanner
            onScan={handleScan}
            onClose={() => {
              setShowScanner(false);
              setScanError('');
              setSuccessMessage('');
              setIsScanning(false);
            }}
            isScanning={isScanning}
            successMessage={successMessage}
            error={scanError}
          />
        )}
      </div>
    );
  }

  // Phase: Revealing start character and target
  if (phase === 'revealing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-dark">
        <div className="scanline"></div>
        <div className="text-center max-w-md">
          {/* Start Character Reveal */}
          <div className="mb-8 animate-pulse">
            <div className="text-6xl mb-4">✓</div>
            <div className="text-cyber-blue text-sm font-mono mb-2">SCANNED:</div>
            {startCharacter?.image && (
              <div className="mb-4 flex justify-center">
                <img 
                  src={startCharacter.image} 
                  alt={startCharacter.name}
                  className="w-32 h-32 object-cover rounded-lg border-2 border-primary shadow-neon-green"
                />
              </div>
            )}
            <div className="text-5xl font-bold text-primary font-mono mb-2 glitch">
              {startCharacter?.name?.toUpperCase()}
            </div>
            <div className="text-cyber-blue text-lg font-mono">
              FLOOR {startCharacter?.floor}
            </div>
          </div>

          {/* Target Character Reveal (fades in after delay) */}
          {showTarget && (
            <div className="animate-fade-in border-t border-primary/30 pt-8">
              <div className="text-cyber-red text-sm font-mono mb-2 animate-pulse">TARGET ASSIGNED:</div>
              {targetCharacter?.image && (
                <div className="mb-4 flex justify-center">
                  <img 
                    src={targetCharacter.image} 
                    alt={targetCharacter.name}
                    className="w-32 h-32 object-cover rounded-lg border-2 border-cyber-red shadow-neon-red"
                  />
                </div>
              )}
              <div className="text-5xl font-bold text-cyber-red font-mono mb-2 glitch">
                {targetCharacter?.name?.toUpperCase()}
              </div>
              <div className="text-cyber-blue text-lg font-mono mb-4">
                FLOOR {targetCharacter?.floor}
              </div>
              <div className="text-yellow-400 text-xl font-mono font-bold">
                {formatTime(timeRemaining)}
              </div>
              <div className="text-primary/50 text-xs font-mono mt-2">
                Starting in 3...2...1...
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Phase: Navigating to target
  if (phase === 'navigating') {
    const isLowTime = timeRemaining <= 30;
    const isCriticalTime = timeRemaining <= 10;

    // Determine floor boundaries based on route
    const minFloor = 1;
    const maxFloor = 5;
    const canGoUp = currentFloor < maxFloor;
    const canGoDown = currentFloor > minFloor;

    return (
      <div className="min-h-screen flex flex-col p-4">
        <div className="scanline"></div>
        
        {/* Timer - Large and prominent */}
        <div className={`text-center py-4 mb-2 ${isCriticalTime ? 'animate-pulse' : ''}`}>
          <div className={`text-6xl font-bold font-mono ${
            isCriticalTime ? 'text-cyber-red' : isLowTime ? 'text-yellow-400' : 'text-primary'
          }`}>
            {formatTime(timeRemaining)}
          </div>
        </div>

        {/* Target - Clear and minimal */}
        <div className="text-center mb-4">
          <div className="text-cyber-blue text-sm font-mono mb-1">TARGET:</div>
          <div className="text-4xl font-bold text-primary font-mono">
            {targetCharacter?.name?.toUpperCase()}
          </div>
          <div className="text-cyber-blue text-lg font-mono">
            FLOOR {targetCharacter?.floor}
          </div>
        </div>

        {/* Floor Navigation */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={() => canGoDown && setCurrentFloor(currentFloor - 1)}
            disabled={!canGoDown}
            className={`px-6 py-3 font-bold font-mono text-lg rounded-lg border-2 transition-all
              ${canGoDown 
                ? 'bg-cyber-blue/20 border-cyber-blue text-cyber-blue hover:bg-cyber-blue hover:text-dark' 
                : 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed'
              }`}
          >
            ▼ DOWN
          </button>
          
          <div className="text-center px-6">
            <div className="text-sm text-cyber-blue font-mono mb-1">CURRENT FLOOR</div>
            <div className="text-5xl font-bold text-primary font-mono">{currentFloor}</div>
          </div>
          
          <button
            onClick={() => canGoUp && setCurrentFloor(currentFloor + 1)}
            disabled={!canGoUp}
            className={`px-6 py-3 font-bold font-mono text-lg rounded-lg border-2 transition-all
              ${canGoUp 
                ? 'bg-cyber-blue/20 border-cyber-blue text-cyber-blue hover:bg-cyber-blue hover:text-dark' 
                : 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed'
              }`}
          >
            ▲ UP
          </button>
        </div>

        {/* Next Waypoint Info or Instruction */}
        {floorPath?.instruction && (
          <div className="text-center mb-3 bg-primary/10 border border-primary/30 rounded-lg p-3">
            {floorPath.nextWaypoint ? (
              <>
                <div className="text-xs text-primary/70 font-mono mb-1">NEXT WAYPOINT:</div>
                <div className="text-xl font-bold text-primary font-mono">
                  {floorPath.nextWaypoint.name.toUpperCase()}
                </div>
                {floorPath.nextWaypoint.type === 'stairs' && (
                  <div className="text-xs text-cyber-blue font-mono mt-1">
                    Connects Floors {floorPath.nextWaypoint.floors.join('-')}
                  </div>
                )}
              </>
            ) : (
              <div className="text-lg font-bold text-cyber-blue font-mono">
                {floorPath.instruction.toUpperCase()}
              </div>
            )}
          </div>
        )}

        {/* Map */}
        <div className="flex-1 bg-dark-lighter rounded-lg border border-primary/30 p-4 mb-4">
          <FloorMap
            floor={currentFloor}
            currentPosition={floorPath?.currentWaypoint}
            targetPosition={floorPath?.nextWaypoint}
            pathNodes={floorPath?.relevantNodes || []}
            floorLayouts={floorLayouts}
          />
        </div>

        {/* Scan Button */}
        <button
          onClick={() => setShowScanner(true)}
          className="bg-primary text-dark font-bold font-mono text-2xl
                   py-6 px-8 rounded-lg hover:shadow-neon-green 
                   transition-all duration-200 border-2 border-primary
                   hover:bg-transparent hover:text-primary w-full"
        >
          SCAN →
        </button>

        {scanError && (
          <div className="mt-4 bg-cyber-red/10 border border-cyber-red text-cyber-red px-4 py-3 rounded-lg font-mono text-center">
            {scanError}
          </div>
        )}

        {showScanner && (
          <QRScanner
            onScan={handleScan}
            onClose={() => {
              setShowScanner(false);
              setScanError('');
              setSuccessMessage('');
              setIsScanning(false);
            }}
            isScanning={isScanning}
            successMessage={successMessage}
            error={scanError}
          />
        )}
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-primary text-2xl font-mono">LOADING...</div>
    </div>
  );
}

export default MissionInterface;
